import React, { type PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@components/main-components/button/Button';

export function ReadMore({
  maxHeight,
  children,
  enabled = true,
}: PropsWithChildren<{ maxHeight: number; enabled?: boolean }>) {
  const childrenRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const [opened, setOpened] = useState(false);
  const divStyle = useMemo(() => {
    if (enabled) {
      if (!opened) {
        return {
          maxHeight,
        };
      } else {
        return {
          maxHeight: contentHeight,
        };
      }
    }
    return {};
  }, [enabled, maxHeight, opened, contentHeight]);

  useEffect(() => {
    if (enabled && childrenRef.current && childrenRef.current.offsetHeight > maxHeight) {
      setOverflowing(true);
      setContentHeight(childrenRef.current.offsetHeight);
    }
  }, [maxHeight, enabled]);

  return (
    <div className="relative">
      <div
        className="overflow-hidden whitespace-pre-wrap break-normal break-words transition-[max-height] duration-200 ease-in-out"
        style={divStyle}
      >
        <div ref={childrenRef}>{children}</div>
      </div>
      {overflowing && !opened && (
        /**
         * Can target with `[&_.read-more-overlay]:hover:from-neutral50`
         */
        <div
          className="read-more-overlay absolute bottom-0 left-0 right-0 flex h-10 items-end justify-start bg-gradient-double-t from-white from-40%"
          data-feed-clickable-div
        >
          <Button
            label="Read more"
            variant="text"
            onClick={() => {
              setOpened(true);
            }}
            size="S"
            className="read-more-button -mx-1 h-auto px-1 py-0 font-base text-base-m font-medium text-base800"
          />
        </div>
      )}
    </div>
  );
}
